<script>
    import {onMount, onDestroy} from 'svelte';
    import {sendEvent} from '../../tracking.js';
    import {asyncQuestionAnsweredEventWithAdTitle} from '../../util.js';

    export let config;
    export let pageId;
    export let changePage;
    export let writeValue;
    const r = 295;
    const x = 46;
    const y = 366;
    const cx = 46 + r;
    const cy = y;
    const degRad = Math.PI / 180;
    const allAnswers = config.answers.reduce((x, item) => x + (item.responses || 0), 0);
    const maxResponses = config.answers.reduce((prev, current) => (prev.responses > (current.responses || 0)) ? prev : current, 0);
    let userSelected = 50;
    let rad = (userSelected - 50) * .01;
    let centerBcr;
    let centerX;
    let centerY;
    let mappedResult;
    let responsesVisible = false;

    function showResponses() {
        responsesVisible = true;
    }

    function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
        var angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    }

    function getCircleSectorPath(startAngle, endAngle, size) {
        /*
        opts = {
            cx              <-- center x
            cy              <-- center y
            radius          <-- circle radius
            start_angle     <-- start angle in degrees
            end_angle       <-- end angle in degrees
        };
        */

        const opts = {
            cx: 340.8106573131876,
            cy: 366,
            radius: 283 * (size ? size : 0),
            start_angle: startAngle - 90,
            end_angle: endAngle - 90,
        };

        const start = polarToCartesian(opts.cx, opts.cy, opts.radius, opts.end_angle);
        const end = polarToCartesian(opts.cx, opts.cy, opts.radius, opts.start_angle);
        const largeArcFlag = opts.end_angle - opts.start_angle <= 180 ? '0' : '1';

        return [
            'M', start.x, start.y,
            'A', opts.radius, opts.radius, 0, largeArcFlag, 0, end.x, end.y,
            'L', opts.cx, opts.cy,
            'Z'
        ].join(' ');
    }

    function normalizeValue(x) {
        if (x < 0 || x > 150) { // value is negative or > 100 if click is in the lower half of the circle; > 150 is the lower left part; > 100 && < 150 is the lower right part
            x = 0;
        }
        if (x > 100) {
            x = 100;
        }

        return x;
    }

    function setValue(x) {
        userSelected = normalizeValue(x);
    }

    function valToDeg(val) {
        return (val - 50) * 1.8 + 270;
    }

    function degToRad(deg) {
        return deg * (Math.PI / 180);
    }

    function getX(val) {
        return cx + r * Math.cos(degToRad(valToDeg(val)));
    }

    function getY(val) {
        return cy + r * Math.sin(degToRad(valToDeg(val)));
    }

    function getMappedResult(value) {
        const keys = Object.keys(config.mapping);
        let mapped = config.mapping[keys[0]];

        keys.forEach(key => {
            if (value >= parseInt(key)) {
                mapped = config.mapping[key];
            }
        });

        return mapped;
    }

    function sendValue(x) {
        x = normalizeValue(x);
        mappedResult = getMappedResult(x);

        showResponses();
        writeValue(`${pageId}`, x);

        if (mappedResult) {
            const payload = {
                flParameters: `#${config.topic}##${mappedResult.fl}##${mappedResult.fl_additional}#`,
                questionId: pageId,
                questionText: config.title,
                answerId: mappedResult.fl,
                answerText: mappedResult.fl,
            };

            sendEvent('questionAnsweredCompass', payload);
            if (mappedResult.next && mappedResult.next !== 'null') {
                asyncQuestionAnsweredEventWithAdTitle(mappedResult, payload);
                changePage(mappedResult.next);
            }
        }
    }

    function setup() {
        centerBcr = document.querySelector('.comapss__center').getBoundingClientRect();
        centerX = centerBcr.x + centerBcr.width / 2;
        centerY = centerBcr.y + centerBcr.height / 2;

        window.addEventListener('resize', setup);

        sendEvent('questionLoaded', {
            questionId: pageId,
            questionText: config.title,
        });
    }

    function calculateMousePosition(e) {
        const deltaX = e.clientX - centerX;
        const deltaY = centerY - e.clientY;
        return 100 - Math.atan2(deltaY, deltaX) * 100 / Math.PI;
    }

    function handleMouse(e) {
        if (typeof centerBcr === 'undefined') {
            setup();
        }

        setValue(calculateMousePosition(e));

        // check for movement once down
        if (!e.currentTarget.__hasMouseMoveListener) {
            e.currentTarget.__hasMouseMoveListener = true;
            e.currentTarget.addEventListener('mousemove', handleMouse, {passive: true});

            // removes movement listener once up
            e.currentTarget.addEventListener('mouseup', function mu(e) {
                e.currentTarget.__hasMouseMoveListener = false;
                e.currentTarget.removeEventListener('mousemove', handleMouse);
                e.currentTarget.removeEventListener('mouseup', mu);

                sendValue(calculateMousePosition(e));
            }, {passive: true});
        }
    }

	function calculateTouchPosition(e) {
        const deltaX = e.changedTouches[0].clientX - centerX;
        const deltaY = centerY - e.changedTouches[0].clientY;
        return 100 - Math.atan2(deltaY, deltaX) * 100 / Math.PI;
    }

    function handleTouch(e) {
        if (typeof centerBcr === 'undefined') {
            setup();
        }

        setValue(calculateTouchPosition(e));

        // check for movement once down
        if (!e.currentTarget.__hasTouchMoveListener) {
            e.currentTarget.__hasTouchMoveListener = true;
            e.currentTarget.addEventListener('touchmove', handleTouch, {passive: true});

            // removes movement listener once up
            e.currentTarget.addEventListener('touchend', function te(e) {
                e.currentTarget.__hasTouchMoveListener = false;
                e.currentTarget.removeEventListener('touchmove', handleTouch);
                e.currentTarget.removeEventListener('touchend', te);

                sendValue(calculateTouchPosition(e));
            }, {passive: true});
        }
    }

    function destroy() {
        window.removeEventListener('resize', setup);
    }

    onMount(setup);
    onDestroy(destroy);
</script>

<style src="Compass.scss"></style>

<!-- bottom left:   translate(46, 366) -->
<!-- top mid:       translate(350.8106573131876, 71.1631790242555) -->
<!-- bottom right:  translate(636, 366) -->
<!-- circle center: translate(350.8106573131876, 366) -->
<!-- radius:        295 -->

<input type="range" min="0" max="100" bind:value={userSelected} hidden>

<!--<pre>-->
<!--value: {userSelected}-->
<!--deg:   {valToDeg(userSelected)}-->
<!--rad:   {degToRad(valToDeg(userSelected))}-->
<!--x:     {getX(userSelected)}-->
<!--y:     {getY(userSelected)}-->
<!--</pre>-->

<section data-page="{pageId}" data-type="{config.type}" class="compass-container">
    <h1>{config.title}</h1>

    <div class="compass__input">
        <svg viewBox="0 0 682 431" on:mousedown={handleMouse} on:touchstart={handleTouch} class="compass">
            <g>
                <svg viewBox="0 0 800 505">
                    <path d="M740 432.9h10C750 220 576 80 400 80 218 80 50 224 50 432.9h10C60 251.1 215 90 400 90c188 0 339.3 162 339.3 342.9"
                          fill="#eaeaea" stroke="#eaeaea" stroke-width="10"></path>
                </svg>
                <path fill="transparent" d="M 46 366 A 1 1, 0, 0, 1, 636 366" stroke="transparent"
                      stroke-width="30"></path>
                <g class={`responses ${responsesVisible ? 'is--visible' : ''}`}>
                    {#each config.answers as answer, i}
                        <g class={`circle-sector ${mappedResult && mappedResult.answer === i ? 'is--selected' : ''}`}>
                            {#if i === 0}
                                <path d={getCircleSectorPath(0, (config.answers[i + 1] ? config.answers[i + 1].value : 100) * 1.8, (answer.responses ? answer.responses : 0) / maxResponses.responses)}/>
                            {:else if i === config.answers.length - 1}
                                <path d={getCircleSectorPath(answer.value * 1.8, 180, (answer.responses ? answer.responses : 0) / maxResponses.responses)}/>
                            {:else}
                                <path d={getCircleSectorPath(answer.value * 1.8, (config.answers[i + 1] ? config.answers[i + 1].value : 100) * 1.8, (answer.responses ? answer.responses : 0) / maxResponses.responses)}/>
                            {/if}
                        </g>
                    {/each}
                </g>
                <g>
                    <g transform="translate(600.3845781653455, 331)">
                        <circle r="35" fill="#FFF" cx="35" cy="35" stroke="#303133"
                                stroke-width="10" class="compass-end-circle"></circle>
                    </g>
                    <g transform="translate(11.81113325896689, 331)">
                        <circle r="35" fill="#FFF" cx="35" cy="35" stroke="#303133"
                                stroke-width="10" class="compass-end-circle"></circle>
                    </g>
                </g>
            </g>
            <g>
                <g transform="translate(341, 366) rotate({valToDeg(userSelected) - 270}) translate(-33,-294) scale(0.5)">
                    <path d="M123.5 600c0-23.1-13.8-43-33.6-52L79.7 131.9c-.2-7.4-6.1-13.2-13.2-13.2s-13.1 5.9-13.2 13.2L43.1 548c-19.8 8.9-33.6 28.8-33.6 52 0 22.4 12.9 41.7 31.7 51.1 14.236 6.546 33.439 7.811 50.6 0 18.8-9.4 31.7-28.7 31.7-51.1z"
                          fill="#303133"></path>
                </g>
                <g>
                    <g transform="translate(301 326)">
                        <circle cx="40" cy="40" r="43" fill="#eaeaea" class="comapss__center"></circle>
                    </g>
                </g>
            </g>
            <g tabindex="0" focusable="true" transform="translate({getX(userSelected)}, {getY(userSelected)})">
                <g cursor="pointer">
                    <circle r="45" stroke-width="3" class="compass-user-circle"></circle>
                    <circle r="40" stroke-width="3" class="compass-user-circle"></circle>
                    <svg x="-20" y="-20" width="40" height="40" viewBox="0 0 40 40">
                        <path d="M19.38,0c2.42,0,3.75-.06,5.92.9s3.97,2.87,4.52,5.18c.35,1.47.08,3.59.54,5.04.3.94,1.32.31,1.15,3.01-.05.78-.5,1.92-.96,2.55-.63.86-.44,1.66-.39,2.59.1,1.78-.52,2.32-1.4,3.87s-1.96,2.83-3.08,4.14c-.07.09-.12.2-.13.31-.05.71-.18,2.01.56,3.08.54.79,1.42,1.28,2.28,1.7,1.61.8,4.62,1.48,6.33,2.03,1.14.37,2.39.74,3.08,1.73.67.96.4,1.55.41,3.14,0,.4-.32.73-.72.73H2.51c-.4,0-.73-.33-.72-.73.01-1.58-.27-2.18.41-3.14.69-.98,1.94-1.36,3.08-1.73,1.71-.55,4.72-1.23,6.33-2.03.86-.43,1.73-.91,2.28-1.7.73-1.07.61-2.37.56-3.08,0-.11-.05-.22-.13-.31-1.12-1.31-2.23-2.64-3.08-4.14s-1.5-2.1-1.4-3.87c.05-.92.24-1.73-.39-2.59-.46-.63-.91-1.77-.96-2.55-.18-2.7.84-2.07,1.15-3.01.46-1.44.19-3.56.54-5.04.55-2.31,2.35-4.21,4.52-5.18S18.19,0,20.62,0"
                              fill="#FFFFFF" stroke="transparent"></path>
                    </svg>
                </g>
            </g>
        </svg>
        {#if responsesVisible}
            <ul class="answer__values">
                {#each config.answers as answer, i}
                    <li>
                        {`${((answer.responses || 0) * 100 / allAnswers).toFixed(0)}%`}
                    </li>
                {/each}
            </ul>
        {/if}
    </div>

    <div class="answers">
        <div class="answer answer--left">{config.answers[0].label}</div>
        <div class="answer answer--right">{config.answers[config.answers.length - 1].label}</div>
    </div>

    <div class="result__count">
        {allAnswers} Teilnehmer
    </div>
</section>
